import { Separator, Text } from '@flatfile/baseline'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useState, useEffect, useCallback } from 'react'

import { EnvironmentAPIKeys, getEnvironmentApiKeys } from '../../components/EnvironmentData/EnvironmentAPIKeys'
import { EnvironmentSecrets, getEnvironmentSecrets } from '../../components/EnvironmentData/EnvironmentSecrets'
import { useIdentity } from '../../hooks/useIdentity'
import { NoAccessError } from '../../components/Shared/CustomErrorComponent'
import { logger } from '../../utils/logging'
import TrialBanner from '../../components/Shared/TrialBanner'

export default function APIKeysAndSecretsPage() {
  const { environment, hasEnvAccess } = useActiveEnvironment()
  const { hasPermission } = useIdentity()
  const hasSecretAccess = hasPermission({ resource: environment, permissionKey: 'secret:create' })
  const [loading, setLoading] = useState(true)
  const [apiKeys, setApiKeys] = useState<any>(null)
  const [secretsList, setSecretsList] = useState<any[]>([])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      // Always fetch API keys
      const keys = await getEnvironmentApiKeys(environment.id)
      setApiKeys(keys)

      // Only fetch secrets if user has access
      if (hasSecretAccess) {
        const secrets = await getEnvironmentSecrets(environment.id)
        setSecretsList(secrets)
      }
    } catch (error) {
      logger.error('Error fetching data', { error })
    } finally {
      setLoading(false)
    }
  }, [environment.id, hasSecretAccess])

  useEffect(() => {
    if (hasEnvAccess) {
      fetchData()
    }
  }, [fetchData, hasEnvAccess])

  if (!hasEnvAccess) return <NoAccessError page='apiKeysAndSecrets' />

  return (
    <div className='w-full h-full flex flex-col overflow-auto p-3'>
      <div className='p-3 flex justify-between'>
        <Text variant='title2'>API Keys{hasSecretAccess && ' & Secrets'}</Text>
        <TrialBanner />
      </div>
      <div className='min-h-[200px]'>
        {loading ? (
          <div className='p-6'>Loading...</div>
        ) : (
          <>
            <EnvironmentAPIKeys environment={environment} apiKeys={apiKeys} />
            {hasSecretAccess && (
              <>
                <Separator className='my-4' />
                <EnvironmentSecrets environment={environment} secrets={secretsList} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
